import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { supabase } from "../lib/supabase";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabaseConfig";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRoleLoading, setIsRoleLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isMediaBuyer, setIsMediaBuyer] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [profile, setProfile] = useState(null);

  // Fetch user on mount
  useEffect(() => {
    const fetchSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setLoading(false); // Set loading to false after initial session check
    };
    fetchSession();

    // Listen for auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Fetch profile and check email verification when user changes
  useEffect(() => {
    if (user) {
      const fetchProfile = async () => {
        try {
          console.log("🔍 Fetching profile for user:", user.id);
          const { data, error } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", user.id)
            .single();

          console.log("🔍 Raw profile data from database:", data);
          console.log("🔍 Profile user_type value:", data?.user_type);

          if (error && error.code !== "PGRST116") {
            // PGRST116 = no rows found
            console.error("Error fetching profile:", error);
            // If profiles table doesn't exist or other error, set default values
            setProfile(null);
            setIsAdmin(false);
            setIsOwner(false);
            setIsMediaBuyer(false);
            setIsEmailVerified(false);
            setIsRoleLoading(false);
            return;
          }

          if (data) {
            console.log("AuthContext - Profile loaded:", data);
            console.log("AuthContext - is_admin value:", data.is_admin);

            // Update all states together and complete role loading
            setProfile(data);

            // Check for owner status first (highest priority)
            // OWNER PRIORITY: Owner has all privileges and overrides everything else
            const isUserOwner = data.is_owner || data.user_type === "owner";
            setIsOwner(isUserOwner);

            // Check for admin status using both fields for robustness
            // ADMIN PRIORITY: If user is admin OR owner, they get admin privileges
            const isUserAdmin =
              isUserOwner || data.is_admin || data.user_type === "admin";
            setIsAdmin(isUserAdmin);

            console.log("🔍 Checking owner status:");
            console.log("  data.is_owner:", data.is_owner);
            console.log("  data.user_type:", data.user_type);
            console.log("  Final isOwner result:", isUserOwner);

            console.log("🔍 Checking admin status:");
            console.log("  data.is_admin:", data.is_admin);
            console.log("  data.user_type:", data.user_type);
            console.log("  Final isAdmin result:", isUserAdmin);

            console.log("🔍 Checking user_type for isMediaBuyer:");
            console.log("  data.user_type:", data.user_type);
            console.log("  typeof data.user_type:", typeof data.user_type);
            console.log(
              "  data.user_type === 'media_buyer':",
              data.user_type === "media_buyer"
            );
            console.log(
              "  data.user_type === 'media_buyer' result:",
              data.user_type === "media_buyer"
            );

            // OWNER/ADMIN PRIORITY: Only set media buyer if NOT owner/admin (owner/admin takes priority over everything)
            // This ensures owner+affiliate, admin+affiliate or admin+media_buyer users go to admin dashboard
            setIsMediaBuyer(!isUserAdmin && data.user_type === "media_buyer");
            setIsEmailVerified(data.email_verified || false);
            setIsRoleLoading(false); // Set loading to false here after successful load
            console.log("AuthContext - Setting isRoleLoading to false");

            console.log(
              "AuthContext - Setting isAdmin to:",
              data.is_admin || false
            );
            console.log(
              "AuthContext - Setting isMediaBuyer to:",
              data.user_type === "media_buyer"
            );
            return; // Early return to avoid the finally block
          } else {
            // Profile doesn't exist - this is an error state
            console.error("🚨 Profile not found for user:", user.id);
            console.error(
              "🚨 This should not happen - profile should be created during registration"
            );
            console.error(
              "🚨 User should re-register to create proper profile"
            );

            // Don't create a default profile - let the user re-register
            setProfile(null);
            setIsAdmin(false);
            setIsOwner(false);
            setIsMediaBuyer(false);
            setIsEmailVerified(false);
            setIsRoleLoading(false);
            return; // Early return to avoid finally block
          }
        } catch (error) {
          console.error("Error in fetchProfile:", error);
          // Set defaults if any error occurs
          setProfile(null);
          setIsAdmin(false);
          setIsOwner(false);
          setIsMediaBuyer(false);
          setIsEmailVerified(false);
          setIsRoleLoading(false);
        }
        // No finally block needed since we handle setIsRoleLoading(false) in each path
      };
      fetchProfile();
    } else {
      // No user - clear all role data and stop loading
      console.log("AuthContext - No user, clearing role data");
      setProfile(null);
      setIsAdmin(false);
      setIsOwner(false);
      setIsMediaBuyer(false);
      setIsEmailVerified(false);
      setIsRoleLoading(false);
    }
  }, [user]);

  // Check email verification status
  const checkEmailVerification = useCallback(async () => {
    if (!user?.id) return false;

    try {
      const { data, error } = await supabase.rpc("is_email_verified", {
        user_id: user.id,
      });

      if (error) {
        console.error("Error checking email verification:", error);
        // If the function doesn't exist (migration not applied), assume verified
        if (
          error.message?.includes("function") ||
          error.message?.includes("does not exist")
        ) {
          console.log(
            "Email verification function not found, assuming verified"
          );
          setIsEmailVerified(true);
          return true;
        }
        return false;
      }

      setIsEmailVerified(data || false);
      return data || false;
    } catch (error) {
      console.error("Error checking email verification:", error);
      // On any error, assume verified to allow normal operation
      setIsEmailVerified(true);
      return true;
    }
  }, [user?.id]);

  // Send verification email via Edge Function
  const sendVerificationEmail = useCallback(async () => {
    if (!user?.id) {
      throw new Error("User not logged in");
    }

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/send-verification-email`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: user.email,
            username: user.user_metadata?.username || profile?.username,
            action: "verification",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send verification email");
      }

      return {
        success: true,
        message: "Verification email sent successfully! 📧 Check your inbox.",
      };
    } catch (error) {
      console.error("Error sending verification email:", error);
      throw error;
    }
  }, [user?.id, user?.email, user?.user_metadata?.username, profile?.username]);

  // Login function - let components handle email verification after profile loads
  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Don't check email verification immediately - let components handle it
      // after profile is loaded to avoid race conditions
      return {
        success: true,
      };
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setIsOwner(false);
    setIsMediaBuyer(false);
    setIsEmailVerified(false);
    setLoading(false);
  }, []);

const googleSSOLogin = useCallback(async (credential) => {
  setLoading(true);

  try {
    // Step 1: Sign in to Supabase with Google token
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: credential,
    });

    if (error) throw error;

    const user = data?.user;
    if (!user) throw new Error("No user returned from Supabase after SSO");

    // Step 2: Check if profile already exists
    

  } catch (error) {
    console.error("❌ Google SSO login error:", error);
    throw error;
  } finally {
    setLoading(false);
  }
}, []);

    
  // Register function with email verification
  const register = useCallback(
    async (
      username,
      email,
      password,
      referralCode = null,
      userType = "affiliate"
    ) => {
      setLoading(true);
      console.log("📝 AuthContext register function called with:");
      console.log("  username:", username);
      console.log("  email:", email);
      console.log("  referralCode:", referralCode);
      console.log("  userType:", userType);
      try {
        // Register with Supabase Auth but completely disable email confirmation
        // This prevents Supabase from sending any native emails
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: null, // Explicitly disable Supabase email confirmation
            data: {
              username: username,
            },
          },
        });

        if (error) throw error;

        // Create profile in database
        if (data.user) {
          try {
            // Check if profile already exists
            console.log(
              "🔍 Checking if profile already exists for user:",
              data.user.id
            );
            const { data: existingProfile, error: checkError } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", data.user.id)
              .single();

            console.log("🔍 Existing profile check result:", {
              existingProfile,
              checkError,
            });

            // Create/update profile immediately (let DB handle timestamps)
            console.log("💾 Creating profile with userType:", userType);
            const profileData = {
              id: data.user.id,
              email: email,
              username: username,
              is_admin: false,
              email_verified: false, // We'll verify this later
              user_type: userType,
            };
            console.log("💾 Profile data to insert:", profileData);

            const { data: insertedProfile, error: profileError } =
              await supabase.from("profiles").upsert(profileData).select();

            console.log("💾 Profile upsert result:", {
              insertedProfile,
              profileError,
            });

            if (profileError) {
              console.error("💥 PROFILE UPSERT FAILED:");
              console.error("  Error code:", profileError.code);
              console.error("  Error message:", profileError.message);
              console.error("  Error details:", profileError.details);
              console.error("  Full error:", profileError);
            }

            if (profileError) {
              console.error("Profile creation error:", profileError);

              // If user already exists but with different user_type, update it
              if (existingProfile && existingProfile.user_type !== userType) {
                console.log(
                  "🔄 Updating existing profile user_type from",
                  existingProfile.user_type,
                  "to",
                  userType
                );
                const { data: updatedProfile, error: updateError } =
                  await supabase
                    .from("profiles")
                    .update({ user_type: userType })
                    .eq("id", data.user.id)
                    .select()
                    .single();

                console.log("🔄 Profile update result:", {
                  updatedProfile,
                  updateError,
                });

                if (!updateError) {
                  console.log(
                    "✅ Successfully updated user_type to:",
                    userType
                  );
                  // Continue with the registration flow
                }
              }

              // If username already exists, try with a unique suffix
              if (
                profileError.code === "23505" &&
                profileError.message?.includes("username")
              ) {
                const uniqueUsername = `${username}_${Date.now().toString().slice(-4)}`;
                console.log(
                  "🔄 Retrying with unique username:",
                  uniqueUsername,
                  "and userType:",
                  userType
                );
                const retryProfileData = {
                  id: data.user.id,
                  email: email,
                  username: uniqueUsername,
                  is_admin: false,
                  email_verified: false,
                  user_type: userType,
                };
                console.log("🔄 Retry profile data:", retryProfileData);
                const { data: retryInserted, error: retryError } =
                  await supabase
                    .from("profiles")
                    .upsert(retryProfileData)
                    .select();

                console.log("🔄 Retry upsert result:", {
                  retryInserted,
                  retryError,
                });
                if (retryError) {
                  console.error("Retry profile creation error:", retryError);
                }
              }
            }

            // Create referral connection if referral code provided
            if (referralCode) {
              try {
                console.log(
                  `🔗 Creating referral connection: ${referralCode} → ${username}`
                );
                console.log("  Referrer (referralCode):", referralCode);
                console.log("  Referee (new user):", username);
                console.log("  Referee email:", email);

                // Import referralService at the top level (will add import statement)
                const { referralService } = await import(
                  "../lib/referralService"
                );

                const referralResult =
                  await referralService.createReferralConnectionOnSignup(
                    referralCode, // referrer
                    username, // referee (new user)
                    email // referee email
                  );

                console.log("  ✅ Referral connection result:", referralResult);
                console.log(
                  "  ✅ Referral connection created successfully during registration"
                );
              } catch (referralError) {
                console.error(
                  "  ❌ Error creating referral connection during registration:",
                  referralError
                );
                console.error("  ❌ Referral error details:", {
                  message: referralError.message,
                  code: referralError.code,
                  details: referralError.details,
                });
                // Don't fail registration if referral fails, but log it
              }
            } else {
              console.log(
                "🔗 No referral code provided, skipping referral connection"
              );
            }

            // Send verification email via Edge Function
            console.log(
              "📧 Sending signup verification email via Edge Function..."
            );
            try {
              const response = await fetch(
                `${SUPABASE_URL}/functions/v1/send-verification-email`,
                {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    email: email,
                    username: username,
                    action: "verification", // CRITICAL: This ensures signup verification email
                  }),
                }
              );

              const emailData = await response.json();
              console.log("📧 Email sending response:", {
                status: response.status,
                ok: response.ok,
                data: emailData,
              });

              if (!response.ok) {
                console.error(
                  "❌ Failed to send verification email:",
                  emailData.error
                );
                return {
                  success: true,
                  message:
                    "Account created successfully! ⚠️ We couldn't send a verification email, but you can log in and request it later.",
                  requiresVerification: true,
                };
              }

              console.log("✅ Signup verification email sent successfully");
              return {
                success: true,
                message:
                  "Account created successfully! 🎉 Check your email for verification link.",
                requiresVerification: true,
              };
            } catch (emailError) {
              console.error("❌ Error sending verification email:", emailError);
              return {
                success: true,
                message:
                  "Account created successfully! ⚠️ We couldn't send a verification email, but you can log in and request it later.",
                requiresVerification: true,
              };
            }
          } catch (emailError) {
            console.error("Error in registration process:", emailError);
            return {
              success: true,
              message: "Account created successfully! You can now log in.",
              requiresVerification: true,
            };
          }
        }

        return {
          success: true,
          message: "Account created successfully!",
          requiresVerification: false,
        };
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Verify email token
  const verifyEmail = useCallback(
    async (token) => {
      try {
        const { data, error } = await supabase.rpc("verify_email", {
          verification_token: token,
        });

        if (error) {
          throw error;
        }

        if (data?.success) {
          // Update local state
          setIsEmailVerified(true);
          if (profile) {
            setProfile({ ...profile, email_verified: true });
          }

          // Send welcome email via Edge Function (server-side)
          if (user?.email) {
            try {
              const response = await fetch(
                `${SUPABASE_URL}/functions/v1/send-verification-email`,
                {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    email: user.email,
                    username:
                      user.user_metadata?.username || profile?.username,
                    action: "welcome",
                  }),
                }
              );

              const emailResult = await response.json();

              if (!response.ok) {
                console.error(
                  "Error sending welcome email:",
                  emailResult.error || emailResult
                );
              }
            } catch (emailError) {
              console.error("Error sending welcome email:", emailError);
            }
          }

          return {
            success: true,
            message: "Email verified successfully!",
          };
        } else {
          throw new Error(data?.error || "Verification failed");
        }
      } catch (error) {
        console.error("Error verifying email:", error);
        throw error;
      }
    },
    [user?.email, user?.user_metadata?.username, profile]
  );

  const value = {
    user,
    profile,
    loading,
    login,
    logout,
    register,
    isAdmin,
    isOwner,
    isMediaBuyer,
    isRoleLoading,
    isEmailVerified,
    checkEmailVerification,
    sendVerificationEmail,
    verifyEmail,
    googleSSOLogin,

  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
